import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserService } from '@rentigo/ngx-authorization';
import { BehaviorSubject, Observable, catchError, map, of, shareReplay, switchMap, tap, timeout } from 'rxjs';
import { ConfigService } from '../../../shared/config/config.service';
import { ManagerSettings } from '../models/settings.model';

export interface ManagerAccess {
  settings: ManagerSettings;
  rankingsEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class ManagerSettingsService {
  private config;
  private settingsRequest?: Observable<ManagerSettings>;
  private rankingsRequest?: Observable<boolean>;
  private userId?: string;
  private generation = 0;
  private readonly settingsSubject = new BehaviorSubject<ManagerSettings | null>(null);
  private readonly rankingsSubject = new BehaviorSubject<boolean>(false);
  readonly settings$ = this.settingsSubject.asObservable();
  readonly rankingsEnabled$ = this.rankingsSubject.asObservable();

  constructor(configService: ConfigService, private http: HttpClient, private userService: UserService) {
    this.config = configService.getConfig()!;
  }

  getSettings(): Observable<ManagerSettings> {
    const user = this.userService.getUser();
    if (!user.isAuthenticated) {
      this.clear();
      return of({});
    }
    if (this.userId !== user.userId) {
      this.clear();
      this.userId = user.userId;
    }
    if (!this.settingsRequest) {
      const generation = this.generation;
      this.settingsRequest = this.http.get<ManagerSettings>(`${this.config.securityDepositBaseUrl}/manager/settings`).pipe(
        tap(settings => {
          if (generation === this.generation) this.settingsSubject.next(settings);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.settingsRequest;
  }

  getAccess(): Observable<ManagerAccess> {
    return this.getSettings().pipe(switchMap(settings => {
      if (!settings.rights) return of({ settings, rankingsEnabled: false });
      return this.getRankingsEnabled().pipe(map(rankingsEnabled => ({ settings, rankingsEnabled })));
    }));
  }

  refresh(): Observable<ManagerAccess> {
    this.clear();
    return this.getAccess();
  }

  clear(): void {
    this.generation++;
    this.settingsRequest = undefined;
    this.rankingsRequest = undefined;
    this.userId = undefined;
    this.settingsSubject.next(null);
    this.rankingsSubject.next(false);
  }

  private getRankingsEnabled(): Observable<boolean> {
    if (!this.rankingsRequest) {
      const now = new Date();
      const startMonth = Math.floor(now.getMonth() / 3) * 3;
      const toDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const params = {
        startDate: toDate(new Date(now.getFullYear(), startMonth, 1)),
        endDate: toDate(new Date(now.getFullYear(), startMonth + 3, 0)),
        pageNo: 1,
        pageSize: 1
      };
      const generation = this.generation;
      this.rankingsRequest = this.http.get<{ isUtilizationRankingEnabled?: boolean }>(
        `${this.config.securityDepositBaseUrl}/manager/utilization-rankings`, { params }
      ).pipe(
        timeout(10000),
        map(result => result.isUtilizationRankingEnabled === true),
        catchError(() => of(false)),
        tap(enabled => {
          if (generation === this.generation) this.rankingsSubject.next(enabled);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.rankingsRequest;
  }
}
